import React from "react";
import type { FFProviderConfig } from "@getbread/feature-flag-sdk-react";
import {
	FeatureFlagProvider,
	useFeatureFlagContext,
	useFeatureFlags,
} from "@getbread/feature-flag-sdk-react";
import { v4 as uuidv4 } from "uuid";

import { getConfig } from "merchant-portal-frontend/src/config";

export interface FeatureFlags {
	// Enables the program selector that works globally across targeting export,
	// settlement report, and transaction list pages
	isGlobalMultiProgramEnabled: boolean;
	// Turns on Complex Hierarchy features of merchant chooser, groups / locations chooser,
	// and include sub merchants checkbox
	isMerchantChooserEnabled: boolean;
	// Enables the merchant ID (MID) validation page for the MLO phase 2 project
	isMerchantIdValidationPageEnabled: boolean;
	// Enables the location page with iteration 1 features (search)
	isLocationPageEnabled: boolean;
	// Enables the compare sub merchant payment products feature for Complex Hierarchies
	isComparePaymentProductsEnabled: boolean;
	// Enables the location page iteration 2 features (filtering, signage)
	isLocationsPageIterationTwoEnabled: boolean;
	// Enables the location status toggle in the location page
	isLocationStatusToggleEnabled: boolean;
	// Enables the CPPv2 experience - scheduled payment products, duplication, editing, etc.
	// This is called "isCPPv2Enabled" in LD, but doesn't parse that way in the portal
	isCpPv2Enabled: boolean;
	// Enables the past year data insights in the Data Insights view
	isPastYearDataInsightsEnabled: boolean;
	// Enables the Hybrid Line of Credit payment product throughout the portal
	isHybridLineOfCreditEnabled: boolean;
	// Enables the Deferred Interest payment product throughout the portal
	isDeferredInterestEnabled: boolean;
	// Enables Advanced Search for the Transaction page
	isAdvancedSearchEnabled: boolean;
	// Enables the new user type tracking (user_type) in Adobe analytics instead of the user email field
	isUserTypeTrackingEnabled: boolean;
	// Enables session storage for Set Password form.
	isPasswordFormSessionStorageEnabled: boolean;
}

// Feature flag configuration
const featureFlagConfig: FFProviderConfig = {
	apiOrigin: getConfig("apiurl"),
	clientSideId: uuidv4(), // Cody suggested a random uuid for now since this is not implemented in the sdk at the moment.
	context: {
		tenantId: "",
	},
	tag: "Merchant_Portal_Frontend",
};

export const convertFlagsToBooleans = (flags: Record<string, string>): FeatureFlags => {
	return Object.entries(flags).reduce((acc, [key, value]) => {
		acc[key as keyof FeatureFlags] = value === "true";
		return acc;
	}, {} as FeatureFlags);
};

export const useFlags = (): FeatureFlags => {
	const flags = useFeatureFlags();

	// Convert the flags to booleans after they're fetched
	return convertFlagsToBooleans(flags);
};

export const useFlagsReady = (): boolean => {
	const { loading } = useFeatureFlagContext();
	return !loading;
};

export const withFeatureFlagProvider = (WrappedComponent: React.ComponentType) => {
	return () => (
		<FeatureFlagProvider {...featureFlagConfig}>
			<WrappedComponent />
		</FeatureFlagProvider>
	);
};
